'use client';

import * as React from 'react';
import { useTranslations } from 'next-intl';
import type {
  SustainabilityInnerSlug,
  SustainabilityRedirectionCard,
} from '@/types/sustainability';
import { ArrowDown } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { scrollToHashTarget } from '@/components/util/GsapSmoothScroller';
import { stripHtml } from '@/lib/homepage-mappers';

export interface SustainabilityPillarCardsProps {
  locale: string;
  cards?: SustainabilityRedirectionCard[];
}

export default function SustainabilityPillarCards({
  locale,
  cards,
}: SustainabilityPillarCardsProps) {
  const t = useTranslations('sustainability.pillars');
  const tCta = useTranslations('sustainability');

  const introRef = React.useRef<HTMLParagraphElement | null>(null);

  const items: { slug: SustainabilityInnerSlug; index: string }[] = [
    { slug: 'environment', index: '01' },
    { slug: 'social', index: '02' },
    { slug: 'governance', index: '03' },
  ];

  // Slug + index are derived locally. CMS supplies card heading and content.
  // CTA scrolls to the matching stand section anchor in the same page.
  const cardCmsBySlug = (() => {
    const map: Record<string, SustainabilityRedirectionCard | undefined> = {};
    items.forEach((it, idx) => {
      map[it.slug] = cards?.[idx];
    });
    return map;
  })();

  React.useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    gsap.registerPlugin(ScrollTrigger);
    const el = introRef.current;
    if (!el) return;

    const words = Array.from(el.querySelectorAll<HTMLElement>('[data-pillars-word]'));
    if (!words.length) return;

    const ctx = gsap.context(() => {
      gsap.set(words, { color: 'rgba(255,255,255,0.28)' });
      gsap.to(words, {
        color: 'rgba(255,255,255,1)',
        ease: 'none',
        stagger: 0.02,
        scrollTrigger: {
          trigger: el,
          start: 'top 78%',
          end: 'bottom 56%',
          scrub: 1.2,
        },
      });
    }, el);

    return () => {
      ctx.revert();
    };
  }, []);

  const intro = t('intro');
  const introWords = intro.split(/\s+/).filter(Boolean);
  const scrollToSection = React.useCallback((slug: SustainabilityInnerSlug) => {
    if (typeof window === 'undefined') return;
    scrollToHashTarget(`#${slug}`, true, true);
  }, []);

  return (
    <section
      id="sustainability-pillars"
      className="relative z-10 bg-black pb-10 md:pb-16 lg:pb-20"
      aria-labelledby="sustainability-pillars-heading"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-0">
        <div className="max-w-3xl">
          <p
            ref={introRef}
            className="text-left text-base leading-relaxed text-white sm:text-[1.08rem] md:text-[1.18rem]"
          >
            {introWords.map((w, i) => (
              <span key={i} data-pillars-word>
                {w}{' '}
              </span>
            ))}
          </p>
        </div>
      </div>
      <h2 id="sustainability-pillars-heading" className="sr-only">
        {t('sectionTitle')}
      </h2>
      <div className="container mx-auto px-4 sm:px-6 lg:px-0">
        <div className="mt-8 grid gap-4 sm:grid-cols-2 md:mt-12 md:grid-cols-3 md:gap-5 lg:gap-8">
          {items.map(({ slug, index }) => (
            <article
              key={slug}
              className="flex flex-col rounded-2xl bg-[#1A1A1A] p-5 sm:p-6 md:min-h-[320px] lg:p-8"
            >
              <div className="mb-5 inline-flex h-9 w-9 items-center justify-center rounded-md bg-[#2A2A2A] text-xs font-semibold text-white sm:h-10 sm:w-10 sm:text-sm">
                {index}
              </div>
              <h3 className="text-base font-bold uppercase tracking-wide text-white sm:text-lg lg:text-xl">
                {cardCmsBySlug[slug]?.heading?.trim() || t(`${slug}.title`)}
              </h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-white/85 sm:mt-4 sm:text-[15px] lg:text-base">
                {stripHtml(cardCmsBySlug[slug]?.content) || t(`${slug}.description`)}
              </p>
              <div className="mt-6 sm:mt-8">
                <button
                  type="button"
                  onClick={() => scrollToSection(slug)}
                  className="inline-flex min-h-[42px] w-full items-center justify-center gap-3 rounded-lg bg-[#2A2A2A] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#353535] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-500 sm:w-auto"
                >
                  <span>{tCta('discoverBelow')}</span>
                  <ArrowDown className="shrink-0 opacity-90 w-4 h-4" aria-hidden />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
